/**
 * Level loading: prepare → commit → stream (review F-4), behind the async
 * `level.open` command.
 *
 * 1. Prepare, off-world: stream the manifest, resolve refs, validate against
 *    component schemas and build a detached world. A level saved in this
 *    browser (LevelSaves) opens its saved entities in place of the manifest's. The live world is untouched,
 *    so a failure or a newer open leaves the current level exactly as it was.
 * 2. Commit, in one synchronous batch: swap the world, clear undo history (a map
 *    load in Unreal) and apply the initial selection non-undoably (review F-6).
 * 3. Stream assets into the live level, then load its game-mode module, if any.
 *
 * Every stage is real work, reported through `progress` for the loading card and
 * the Output Log (CLAUDE.md rule 7).
 */
import { isEngineAsset } from "../../assets/engineAssets";
import type { AssetRef, AssetRegistry } from "../../assets/AssetRegistry";
import type {
  ContentIndex,
  LevelIndexEntry,
  LevelManifest,
  ManifestEntity,
} from "../../assets/content";
import type { CommandBus } from "../../commands/CommandBus";
import { parsePayload } from "../../commands/payload";
import type { AsyncCommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import type { Log } from "../../core/Log";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import { findEntity } from "../../world/names";
import type { WorldData, WorldReader, WorldSnapshot } from "../../world/World";
import type { LevelCommitter } from "../../world/WorldModule";
import type { Selection } from "../selection/Selection";
import type { GameModeRegistry } from "../../game/GameModeRegistry";

export type StageStatus = "queued" | "active" | "done" | "failed";
export type StageId = "unload" | "entities" | "meshes" | "levelModule" | "shaders";

export interface LoadStage {
  readonly id: StageId;
  readonly label: string;
  readonly status: StageStatus;
  /** e.g. "64%" while streaming. */
  readonly detail?: string;
}

export interface LoadProgress {
  readonly phase: "idle" | "loading" | "done" | "failed";
  readonly levelId: string | null;
  readonly name: string;
  readonly assetPath: string;
  readonly bytesLoaded: number;
  readonly bytesTotal: number | null;
  readonly stages: readonly LoadStage[];
  readonly error: string | null;
}

export interface OpenLevelPayload {
  readonly id: string;
  /** Entity id or name to select; otherwise nothing is selected, as in Unreal. */
  readonly select?: string;
}

export interface LevelServiceDeps {
  readonly assets: { readonly registry: AssetRegistry; readonly index: ContentIndex };
  readonly levels: LevelCommitter;
  readonly world: WorldReader;
  readonly selection: Selection;
  readonly bus: CommandBus;
  readonly gameModes: GameModeRegistry;
  readonly scheduler: ChangeScheduler;
  readonly log: Log;
  /**
   * Readies the committed level's scene to draw (the renderer's pipelines and
   * selection outline), so its first frames don't stall: the Shaders stage.
   */
  readonly prepareScene: () => Promise<void>;
  /** Saved edits for a version of a level (LevelSaves); none when omitted. */
  readonly saves?: {
    load(levelId: string, hash: string): readonly ManifestEntity[] | null;
  };
}

const IDLE: LoadProgress = {
  phase: "idle",
  levelId: null,
  name: "",
  assetPath: "",
  bytesLoaded: 0,
  bytesTotal: null,
  stages: [],
  error: null,
};

export class LevelService {
  readonly progress: ReadableStore<LoadProgress>;
  private readonly store: VersionedStore<LoadProgress>;

  /** The open level's manifest as authored (before any saved edits), for Reset to Default. */
  private authored: LevelManifest | null = null;

  constructor(private readonly deps: LevelServiceDeps) {
    this.store = new VersionedStore(deps.scheduler, IDLE);
    this.progress = this.store;
  }

  /** The open level's manifest as authored, without the edits saved in this browser. */
  authoredManifest(): LevelManifest | null {
    return this.authored;
  }

  /** Opens a level. Throws on failure; returns early (without committing) if superseded. */
  async open(payload: OpenLevelPayload, signal: AbortSignal): Promise<void> {
    const entry = this.deps.assets.index.levels.find((level) => level.id === payload.id);
    if (!entry) throw new Error(`Level '${payload.id}' not found`);

    const previous = this.deps.world.getSnapshot();
    this.begin(entry, previous);

    try {
      const { data, manifest } = await this.prepare(entry, signal);
      if (signal.aborted) return;

      this.commit(entry, previous, data, payload.select);
      this.authored = manifest;
      await this.streamAssets(data, signal);
      await this.loadGameModes(data);
      await this.prepareScene(signal);
      this.patch({ phase: "done" });
      this.deps.log.display("Level", `opened ${entry.name} · ${data.entities.size} entities`);
    } catch (thrown) {
      if (signal.aborted) return;
      const message = thrown instanceof Error ? thrown.message : String(thrown);
      this.patch({ phase: "failed", error: message });
      this.markActiveStage("failed");
      throw thrown;
    }
  }

  // ─── Stages ────────────────────────────────────────────────────────────

  private begin(entry: LevelIndexEntry, previous: WorldSnapshot): void {
    const stages: LoadStage[] = [];
    const previousName = previous.levelId === null ? null : this.levelName(previous.levelId);
    if (previousName !== null)
      stages.push({ id: "unload", label: `Unload ${previousName}`, status: "queued" });
    stages.push({ id: "entities", label: "Entities", status: "active" });
    stages.push({ id: "meshes", label: "Meshes · textures", status: "queued" });
    stages.push({ id: "shaders", label: "Shaders", status: "queued" });

    this.store.publish({
      ...IDLE,
      phase: "loading",
      levelId: entry.id,
      name: entry.name,
      assetPath: entry.assetPath,
      bytesTotal: entry.bytes,
      stages,
    });
    this.deps.log.display("Level", `streaming ${entry.assetPath}`);
  }

  /** Streams and parses the manifest into a detached world. Nothing live changes here. */
  private async prepare(
    entry: LevelIndexEntry,
    signal: AbortSignal,
  ): Promise<{ data: WorldData; manifest: LevelManifest }> {
    const ref: AssetRef & { kind: "level" } = {
      kind: "level",
      path: entry.path,
      bytes: entry.bytes,
      hash: entry.hash,
    };

    const stopTracking = this.deps.assets.registry.onProgress((event) => {
      if (event.ref.path === entry.path)
        this.patch({ bytesLoaded: event.loaded, bytesTotal: event.total });
    });
    try {
      const manifest = await this.deps.assets.registry.load(ref, { signal });
      const saved = this.deps.saves?.load(entry.id, entry.hash) ?? null;
      const prepared = this.deps.levels.prepare(
        saved ? { ...manifest, entities: saved } : manifest,
      );
      if (saved) this.deps.log.display("Level", "restored the edits saved in this browser");
      prepared.warnings.forEach((warning) => this.deps.log.warning("Level", warning));
      return { data: prepared.data, manifest };
    } finally {
      stopTracking();
    }
  }

  /** One batch: swap the world, clear history, select. React sees a single update. */
  private commit(
    entry: LevelIndexEntry,
    previous: WorldSnapshot,
    data: WorldData,
    select: string | undefined,
  ): void {
    const zones = countWithComponent(data, "Zone");

    this.deps.scheduler.batch(() => {
      if (previous.levelId !== null) {
        this.deps.log.display(
          "Level",
          `unloading ${this.levelName(previous.levelId)} · ${plural(previous.entities.size, "entity", "entities")} released`,
        );
      }
      this.deps.levels.commit(data);
      this.deps.bus.clearHistory();
      this.deps.selection.writer.set(this.initialSelection(data, select));

      this.setStage("unload", { status: "done" });
      this.setStage("entities", {
        status: "done",
        label: `Entities (${plural(zones, "zone", "zones")})`,
      });
      this.setStage("meshes", { status: "active" });
    });

    this.deps.log.display(
      "Stream",
      `${entry.name}.manifest · ${plural(zones, "zone", "zones")} · ${plural(data.entities.size, "entity", "entities")}`,
    );
  }

  /**
   * Streams the models Mesh Renderers show and the Sky's panoramas, in parallel. Placeholders and
   * engine shapes need none (0/0). A model that fails is a warning, never a
   * failed level (A-43): its entity draws nothing, like a missing mesh in Unreal.
   */
  private async streamAssets(data: WorldData, signal: AbortSignal): Promise<void> {
    this.prefetchBackground(data, signal);
    const assets = collectAssets(data);
    let loaded = 0;
    let failed = 0;
    const report = () => {
      this.setStage("meshes", { detail: meshesDetail(loaded, failed, assets.length) });
    };
    report();

    await Promise.all(
      assets.map(async ({ kind, path }) => {
        try {
          await this.deps.assets.registry.load({ kind, path }, { signal });
          loaded++;
        } catch (thrown) {
          if (signal.aborted) return;
          failed++;
          const reason = thrown instanceof Error ? thrown.message : String(thrown);
          this.deps.log.warning("Stream", `couldn't load ${path} (${reason})`);
        }
        report();
      }),
    );
    // Failed models don't throw, so a superseded load has to stop here itself.
    signal.throwIfAborted();
    this.setStage("meshes", { status: "done" });
  }

  /**
   * Starts downloading the models the level loads in the background (a map)
   * now, with everything else, at low priority so the rest gets the bandwidth
   * first. Only their decoding waits: the renderer is handed them once the
   * level is revealed (RenderBridge's `levelLoading`). Not awaited; a failure
   * here is reported when the renderer loads the model.
   */
  private prefetchBackground(data: WorldData, signal: AbortSignal): void {
    for (const path of collectBackgroundModels(data)) {
      this.deps.assets.registry
        .prefetch({ kind: "gltf", path }, { signal, priority: "low" })
        .catch(() => undefined);
    }
  }

  /** Loads the game-mode modules the level's zones name, if registered (S-7). */
  private async loadGameModes(data: WorldData): Promise<void> {
    const modes = [...new Set(collectGameModes(data))].filter((id) => this.deps.gameModes.has(id));
    if (modes.length === 0) return;

    // Before the Shaders stage, which readies the scene last.
    this.insertStageBefore("shaders", {
      id: "levelModule",
      label: `Level module (${modes.join(", ")})`,
      status: "active",
    });
    await Promise.all(modes.map((id) => this.deps.gameModes.load(id)));
    this.setStage("levelModule", { status: "done" });
  }

  /**
   * The Shaders stage: the renderer readies the scene (its pipelines and
   * selection outline) while the load is still on screen, so the level
   * appears ready rather than hitching on its first frames.
   */
  private async prepareScene(signal: AbortSignal): Promise<void> {
    this.setStage("shaders", { status: "active" });
    try {
      await this.deps.prepareScene();
    } catch (thrown) {
      // Readying is an optimization: the level still opens, and draws as it can.
      const reason = thrown instanceof Error ? thrown.message : String(thrown);
      this.deps.log.warning("Level", `couldn't prepare the scene's shaders (${reason})`);
    }
    signal.throwIfAborted();
    this.setStage("shaders", { status: "done" });
  }

  // ─── Helpers ───────────────────────────────────────────────────────────

  /** An explicit selection (id, path or unique name), else nothing (a level opens unselected). */
  private initialSelection(data: WorldData, select: string | undefined) {
    const lookup = select === undefined ? undefined : findEntity(data.entities, select);
    const explicit = lookup && "found" in lookup ? lookup.found : null;
    if (lookup && "missing" in lookup)
      this.deps.log.warning("Level", `no entity '${select}' to select`);
    if (lookup && "ambiguous" in lookup) {
      this.deps.log.warning(
        "Level",
        `'${select}' is ambiguous (${lookup.ambiguous.join(", ")}); nothing selected by name`,
      );
    }

    return explicit ? { ids: [explicit], active: explicit } : { ids: [], active: null };
  }

  /** A level's virtual asset path (`/Roles/Riot_Games.level`, A-27), if the index has it. */
  assetPathOf(levelId: string): string | null {
    return this.deps.assets.index.levels.find((level) => level.id === levelId)?.assetPath ?? null;
  }

  private levelName(levelId: string): string {
    return this.deps.assets.index.levels.find((level) => level.id === levelId)?.name ?? levelId;
  }

  private patch(patch: Partial<LoadProgress>): void {
    this.store.publish({ ...this.store.getSnapshot(), ...patch });
  }

  private setStage(id: StageId, patch: Partial<LoadStage>): void {
    const stages = this.store
      .getSnapshot()
      .stages.map((stage) => (stage.id === id ? { ...stage, ...patch } : stage));
    this.patch({ stages });
  }

  /** Adds a stage just before another, or at the end when that one isn't listed. */
  private insertStageBefore(before: StageId, stage: LoadStage): void {
    const stages = [...this.store.getSnapshot().stages];
    const at = stages.findIndex((existing) => existing.id === before);
    stages.splice(at === -1 ? stages.length : at, 0, stage);
    this.patch({ stages });
  }

  private markActiveStage(status: StageStatus): void {
    const active = this.store.getSnapshot().stages.find((stage) => stage.status === "active");
    if (active) this.setStage(active.id, { status });
  }
}

/** The `level.open` async command (never undoable). */
export function levelCommands(service: LevelService): AsyncCommandDefinition[] {
  const open: AsyncCommandDefinition<OpenLevelPayload> = {
    type: "level.open",
    validate: (raw) => {
      const shape = parsePayload(raw, { id: "string", select: "string?" });
      return shape.ok ? { ok: true, value: shape.value } : shape;
    },
    run: (payload, signal) => service.open(payload, signal),
    // The asset path, as the Output Log shows it (reference 04); unknown ids fall back to the id.
    describe: (payload) => `OpenLevel ${service.assetPathOf(payload.id) ?? payload.id}`,
  };
  return [open];
}

/** The Meshes · textures row: "2/3", with failures called out ("2/3 · 1 failed"). */
function meshesDetail(loaded: number, failed: number, total: number): string {
  const progress = `${loaded}/${total}`;
  return failed === 0 ? progress : `${progress} · ${failed} failed`;
}

function countWithComponent(data: WorldData, type: string): number {
  return [...data.entities.values()].filter((entity) => entity.components.has(type)).length;
}

/** The models Mesh Renderers show; engine shapes are built in and never stream. */
/**
 * What the level waits for: Mesh Renderers' models, except those loaded in
 * the background, and both of a Sky's panoramas (the theme picks one, and a
 * theme switch shouldn't need a download).
 */
function collectAssets(data: WorldData): { kind: "gltf" | "panorama"; path: string }[] {
  const assets: { kind: "gltf" | "panorama"; path: string }[] = [];
  const add = (kind: "gltf" | "panorama", path: unknown) => {
    if (typeof path === "string" && !isEngineAsset(path)) assets.push({ kind, path });
  };

  for (const entity of data.entities.values()) {
    const renderer = entity.components.get("MeshRenderer");
    if (renderer && renderer.background !== true) add("gltf", renderer.mesh);
    const sky = entity.components.get("Sky");
    if (sky) {
      add("panorama", sky.day);
      add("panorama", sky.night);
    }
  }
  return assets;
}

/** Models Mesh Renderers load in the background. */
function collectBackgroundModels(data: WorldData): string[] {
  return [...data.entities.values()].flatMap((entity) => {
    const renderer = entity.components.get("MeshRenderer");
    const path = renderer?.mesh;
    return renderer?.background === true && typeof path === "string" && !isEngineAsset(path)
      ? [path]
      : [];
  });
}

function collectGameModes(data: WorldData): string[] {
  const modes = [...data.entities.values()].map(
    (entity) => entity.components.get("Zone")?.gameMode,
  );
  return modes.filter((mode): mode is string => typeof mode === "string" && mode !== "");
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
